import type { IEventItemProps } from 'src/types/product';

import { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Stack, Button, Typography, Dialog, DialogTitle, DialogContent, Rating } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { fDate, fTime, fIsSame } from 'src/utils/format-time';

import { getAgeInterval, getTime } from 'src/utils';
import { parentLikeActivityMutationFunc } from 'src/context/like/parent_likeActivity';
import { parentUnlikeActivityMutationFunc } from 'src/context/like/parent_unlikeActivity';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  product: IEventItemProps;
  detailsHref: string;
  entityType: string;
};

export function ProductItem({ product, detailsHref, entityType }: Props) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { accessToken } = useSelector((state: any) => state.auth);
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [isLikedActivity, setIsLikedActivity] = useState(false);
  const [openAllDates, setOpenAllDates] = useState(false);
  const [selectedDates, setSelectedDates] = useState([] as any);

  useEffect(() => {
    setHasAccessToken(accessToken);
  }, [accessToken]);

  useEffect(() => {
    setIsLikedActivity(product.likedByParent);
  }, [product]);

  const renderImage = () => (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 132, sm: 240 },
        '&:hover': {
          '& img': {
            transition: 'all 0.5s ease',
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <img
          src={product.listing?.coverImage}
          alt={product.title}
          loading="lazy"
          width="100%"
          height="100%"
          style={{ objectFit: 'cover', cursor: 'pointer' }}
          onClick={() => detailsHref && router.push(detailsHref)}
        />
      </Box>

      <Button
        variant="outlined"
        onClick={() => {
          if (hasAccessToken) {
            if (isLikedActivity) {
              dispatch(
                parentUnlikeActivityMutationFunc({
                  activityId: product._id,
                })
              );
              setIsLikedActivity(false);
            } else {
              dispatch(
                parentLikeActivityMutationFunc({
                  activityId: product._id,
                })
              );
              setIsLikedActivity(true);
            }
          } else {
            router.push(paths.auth.jwt.signIn);
          }
        }}
        sx={{
          position: 'absolute',
          top: 32,
          right: 20,
          transition: 'all 0.5s ease',
          transform: 'translateY(-50%)',
          bgcolor: 'transparent',
          p: 0,
          minWidth: 0,
          width: 40,
          height: 40,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          zIndex: 9,
          color: 'common.white',
          borderColor: 'common.white',
        }}
      >
        <Iconify
          icon={
            isLikedActivity ? 'material-symbols-light:star' : 'material-symbols-light:star-outline'
          }
          width={32}
          sx={{ color: 'white' }}
        />
      </Button>
    </Box>
  );

  const renderContent = () => (
    <>
      <Box sx={{ pt: 1.25, px: 1.5 }}>
        <Typography
          variant="body1"
          fontWeight={600}
          fontFamily="Poppins"
          sx={{ mb: 1, color: 'primary.main', cursor: 'pointer' }}
          onClick={() => detailsHref && router.push(detailsHref)}
        >
          {product.title ?? (product as any)?.listing?.title}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'row' }}
        alignItems={{ xs: 'flex-start' }}
        justifyContent="space-between"
        minHeight={100}
        pb={1.25}
        px={1.5}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5, visibility: product.listing?.rating?.average ? 'visible' : 'hidden' }}
          >
            <Rating
              value={product.listing?.rating?.average}
              readOnly
              size="small"
              precision={0.1}
              sx={{ fontSize: '12px' }}
              color="error"
              
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5 }}
            visibility={!product.plus ? 'visible' : 'hidden'}
          >
            <Iconify
              icon="material-symbols:person-outline-rounded"
              width={20}
              sx={{ color: 'success.main' }}
            />

            <Typography variant="caption">
              {product?.listing?.ageInterval && product?.listing?.ageInterval.length > 0
                ? getAgeInterval(product?.listing?.ageInterval)
                : 'Tüm Yaşlar'}
            </Typography>
          </Stack>

          {product.isBlog && product?.schedule?.openHour && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Iconify icon="tabler:clock" width={20} sx={{ color: 'warning.main' }} />
              <Typography
                variant="caption"
                fontFamily="Rubik"
                fontWeight={400}
                sx={{ color: 'text.secondary' }}
              >
                {product?.schedule?.closeHour
                  ? `${getTime(product?.schedule?.openHour)} - ${getTime(product?.schedule?.closeHour)}`
                  : getTime(product?.schedule?.openHour)}
              </Typography>
            </Stack>
          )}
          {product.plus && (
            <Stack sx={{ mb: 0.5 }}>
              <Typography 
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {product.listing?.description}
              </Typography>
            </Stack>
          )}
          {!product.plus && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{ mb: 0.5 }}
              visibility={product.schedule.startDate ? 'visible' : 'hidden'}
            >
              <Iconify icon="mdi:calendar-outline" width={20} sx={{ color: 'primary.main' }} />

              {product.schedule?.days && product.schedule?.days?.length > 1 ? (
                <Button
                  variant="text"
                  sx={{ p: 0 }}
                  onClick={() => {
                    setSelectedDates(product?.schedule?.days);
                    setOpenAllDates(true);
                  }}
                >
                  <Typography variant="caption">Tarihleri Gör</Typography>
                </Button>
              ) : (
                <Typography variant="caption">
                  {fIsSame(product.schedule.startDate, product.schedule.endDate)
                    ? `${fDate(product.schedule.startDate)}`
                    : `${fDate(product.schedule.startDate)}-${fDate(product.schedule.endDate)}`}
                  {` - `}
                  {product?.schedule?.days?.[0]?.startTime === 100 
                    ? getTime(product?.schedule?.days?.[0]?.startTime)
                    : `${getTime(product?.schedule?.days?.[0]?.startTime)}-${getTime(product?.schedule?.days?.[0]?.endTime)}`}
                </Typography>
              )}
            </Stack>
          )}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ mb: 0.5 }}
            visibility={
              product.location.district || product.location.city ? 'visible' : 'hidden'
            }
          >
            {product.listing.format === 'online' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify
                  icon="proicons:location"
                  width={20}
                  sx={{ color: 'error.main', visibility: 'hidden' }}
                />
                <Typography
                  variant="caption"
                  fontWeight={400}
                  fontFamily="Poppins"
                  sx={{
                    textAlign: 'right',
                    color: 'white',
                    bgcolor: 'success.main',
                    borderRadius: 0.5,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  Online
                </Typography>
              </Box>
            ) : (
              <>
                <Iconify icon="proicons:location" width={20} sx={{ color: 'error.main' }} />
                <Typography variant="caption">
                  {product.location.district === product.location.city
                    ? product.location.city
                    : `${product.location.district}/${product.location.city}`}
                </Typography>
              </>
            )}
          </Stack>
        </Box>

        <Stack
          direction="column"
          alignItems="center"
          justifyContent="flex-end"
          gap={0.5}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            height: '100%',
          }}
        >
          {product.price ? entityType === 'blog' ? null : (
            <Typography
              variant="body2"
              fontWeight={700}
              fontFamily="Poppins"
              sx={{ textAlign: 'right' }}
            >
              {`${product.price}₺`}
            </Typography>
          ) : (!product.plus && product.fee === 'free') ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.main',
                px: 1,
                py: 0.5,
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={400}
                fontFamily="Poppins"
                sx={{ textAlign: 'right', color: 'white' }}
              >
                Ücretsiz
              </Typography>
            </Box>
          ) :  null} 
          <Button
            variant={product?.plus ? 'outlined' : 'contained'}
            size="small"
            color="primary"
            sx={{ mb: 0.25, px: 0.5, py: 0.5, borderRadius: 0.5, fontSize: '12px !important' }}
            onClick={() => detailsHref && router.push(detailsHref)}
          >
            {product?.plus ? 'Devamı' : 'TIKLA'}
          </Button>
        </Stack>
      </Stack>
    </>
  );

  return (
    <Stack
      sx={{
        borderRadius: 2,
        boxShadow: '0px 0px 10px 0px rgba(80, 80, 80, 0.5)',
        overflow: 'hidden',
      }}
    >
      {renderImage()}
      {renderContent()}

      <AnimatePresence>
        {openAllDates && (
          <Dialog
            fullWidth
            maxWidth="xs"
            open={openAllDates}
            onClose={() => setOpenAllDates(false)}
          >
            <DialogTitle id="alert-dialog-title">Tarihleri Gör</DialogTitle>

            <DialogContent sx={{ pb: 3 }}>
              {selectedDates && selectedDates.length > 0 ? (
                <Box>
                  {selectedDates.map((date: any) => (
                    <Box
                      key={date.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {`${fDate(date.date)} - ${date?.startTime === 100 
                          ? getTime(date?.startTime)
                          : `${getTime(date?.startTime)}-${getTime(date?.endTime)}`}`}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2">Tarih seçilmemiş</Typography>
              )}
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
    </Stack>
  );
}
